/**
 * Back-in-stock notifications
 * - <bis-trigger> wraps a button; opens the modal on click
 * - <bis-modal>   wraps a native <dialog>; handles the form + Klaviyo API
 * - KlaviyoClient encapsulates the two-step subscribe (list + variant)
 *
 * Loaded via <script type="module"> from layout/theme.liquid.
 */

const KLAVIYO_REVISION = '2024-10-15';
const KLAVIYO_BASE = 'https://a.klaviyo.com/client';
const SUCCESS_AUTOCLOSE_MS = 2400;

class KlaviyoClient {
  #companyId;

  constructor({ companyId }) {
    this.#companyId = companyId;
  }

  get isConfigured() {
    return Boolean(this.#companyId);
  }

  async subscribeToVariant({ email, variantId }) {
    return this.#post('back-in-stock-subscriptions', {
      type: 'back-in-stock-subscription',
      attributes: {
        channels: ['EMAIL'],
        profile: { data: { type: 'profile', attributes: { email } } },
      },
      relationships: {
        variant: {
          data: { type: 'catalog-variant', id: `$shopify:::$default:::${variantId}` },
        },
      },
    });
  }

  async #post(endpoint, data) {
    const url = `${KLAVIYO_BASE}/${endpoint}/?company_id=${encodeURIComponent(this.#companyId)}`;
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        accept: 'application/json',
        revision: KLAVIYO_REVISION,
      },
      body: JSON.stringify({ data }),
    });
    if (!res.ok && res.status !== 202) {
      throw new Error(`Klaviyo ${res.status}: ${await res.text()}`);
    }
    return res;
  }
}

class BisModal extends HTMLElement {
  #klaviyo;
  #dialog;
  #form;
  #refs = {};
  #subtitleTemplate = '';
  #defaultSubmitText = '';
  #processingText = '';

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');
    this.#form = this.querySelector('form.bis-modal__form');
    this.#refs = {
      variant: this.querySelector('[data-bis-variant]'),
      subtitle: this.querySelector('[data-bis-subtitle]'),
      submit: this.querySelector('[data-bis-submit]'),
      submitLabel: this.querySelector('[data-bis-submit-label]'),
      error: this.querySelector('[data-bis-error]'),
      formWrap: this.querySelector('[data-bis-state="form"]'),
      successWrap: this.querySelector('[data-bis-state="success"]'),
    };

    this.#subtitleTemplate = this.#refs.subtitle?.textContent ?? '';
    this.#defaultSubmitText = this.#refs.submitLabel?.textContent ?? '';
    this.#processingText = this.dataset.processingLabel ?? 'Submitting...';

    this.#klaviyo = new KlaviyoClient({ companyId: this.dataset.companyId });

    this.#form?.addEventListener('submit', (event) => this.#onSubmit(event));
    this.#dialog?.addEventListener('click', (event) => {
      if (event.target === this.#dialog) this.close();
    });
    this.#dialog?.addEventListener('close', () => this.#unlockScroll());
    this.#dialog?.addEventListener('cancel', () => this.#unlockScroll());
  }

  open({ variantId, productTitle }) {
    if (!this.#dialog || !variantId) return;
    this.#refs.variant.value = variantId;
    if (this.#refs.subtitle) {
      this.#refs.subtitle.textContent = this.#subtitleTemplate.replaceAll('%PRODUCT%', productTitle ?? '');
    }
    this.#reset();
    this.#dialog.showModal();
    this.#lockScroll();
    this.#refs.formWrap?.querySelector('input[type="email"]')?.focus();
  }

  close() {
    this.#dialog?.close();
  }

  #lockScroll() {
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    if (scrollbarWidth > 0) {
      document.documentElement.style.setProperty('--bis-scrollbar-width', `${scrollbarWidth}px`);
      document.documentElement.style.paddingRight = `${scrollbarWidth}px`;
    }
    document.documentElement.classList.add('bis-scroll-locked');
  }

  #unlockScroll() {
    document.documentElement.classList.remove('bis-scroll-locked');
    document.documentElement.style.paddingRight = '';
    document.documentElement.style.removeProperty('--bis-scrollbar-width');
  }

  #reset() {
    this.#refs.formWrap.hidden = false;
    this.#refs.successWrap.hidden = true;
    this.#refs.error.hidden = true;
    this.#setLoading(false);
  }

  #setLoading(loading) {
    this.#refs.submit.disabled = loading;
    this.#refs.submit.classList.toggle('is-loading', loading);
    this.#refs.submitLabel.textContent = loading ? this.#processingText : this.#defaultSubmitText;
  }

  #showError() {
    this.#refs.error.hidden = false;
    this.#setLoading(false);
  }

  #showSuccess() {
    this.#refs.formWrap.hidden = true;
    this.#refs.successWrap.hidden = false;
    setTimeout(() => this.close(), SUCCESS_AUTOCLOSE_MS);
  }

  async #onSubmit(event) {
    event.preventDefault();

    const data = new FormData(this.#form);
    const email = String(data.get('email') ?? '').trim();
    const variantId = String(data.get('variant_id') ?? '');

    if (!this.#klaviyo.isConfigured) {
      console.warn(
        '[BIS] Klaviyo company_id is missing.\n' +
        'Open Theme editor → Theme settings → Back in stock and paste your Klaviyo public API key (Klaviyo → Account → API keys).'
      );
      this.#showError();
      return;
    }
    if (!email) {
      console.warn('[BIS] Email field empty.');
      this.#showError();
      return;
    }
    if (!variantId) {
      console.warn('[BIS] Variant id missing on the trigger / hidden input.');
      this.#showError();
      return;
    }

    this.#refs.error.hidden = true;
    this.#setLoading(true);

    try {
      await this.#klaviyo.subscribeToVariant({ email, variantId });
      this.#showSuccess();
    } catch (error) {
      console.error('[BIS] Klaviyo request failed:', error);
      this.#showError();
    }
  }
}

class BisTrigger extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (event) => this.#onClick(event));
  }

  #onClick(event) {
    const button = event.target.closest('button, a');
    if (!button) return;
    event.preventDefault();

    const modal = document.querySelector('bis-modal');
    if (!modal) return;

    modal.open({
      variantId: this.#resolveVariantId(),
      productTitle: this.dataset.productTitle ?? '',
    });
  }

  /** PDP triggers prefer the live form value so swatch picks register the right variant. */
  #resolveVariantId() {
    if (this.dataset.context === 'pdp') {
      const live = document.querySelector('.js-product-form [name="id"]');
      if (live?.value) return live.value;
    }
    return this.dataset.variantId ?? '';
  }
}

customElements.define('bis-modal', BisModal);
customElements.define('bis-trigger', BisTrigger);
